const EXAM_IMAGE_SYNTHETIC_KEY = 'exam-room-images';
const EXAM_ATTACHMENT_SYNTHETIC_KEY = 'exam-room-attachments';

const IMAGE_TIMEOUT = 120000; // 2 minutes for large image sets
const ATTACHMENT_TIMEOUT = 30000; // 30 seconds for attachment downloads
export interface ServiceWorkerMessage {
  type: string;
  payload?: unknown;
}

// cspell:disable-next-line
export interface PrecacheCompletePayload {
  ok: boolean;
  count: number;
  total: number;
  failed: number;
}

// cspell:disable-next-line
export interface PrecacheProgressPayload {
  count: number;
  total: number;
  id: string;
}

export interface CacheStatusPayload {
  count: number;
  keys: string[];
}

export interface SignedImagePayload {
  id: string;
  signedUrl: string;
}

/**
 * Register the service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    // console.warn('[ServiceWorker] Service Workers are not supported in this browser');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    // console.log('[ServiceWorker] Registered successfully:', registration.scope);

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    // console.log('[ServiceWorker] Ready');

    return registration;
  } catch {
    // console.error('[ServiceWorker] Registration failed:', error);
    return null;
  }
}

/**
 * Send a message to the service worker
 */
export function sendMessageToServiceWorker(message: ServiceWorkerMessage): void {
  if (!navigator.serviceWorker.controller) {
    // console.warn('[ServiceWorker] No active service worker controller');
    return;
  }

  navigator.serviceWorker.controller.postMessage(message);
}

/**
 * Listen for messages from the service worker
 */
export function listenToServiceWorker(
  callback: (message: ServiceWorkerMessage) => void,
): () => void {
  const handler = (event: MessageEvent) => {
    callback(event.data);
  };

  navigator.serviceWorker.addEventListener('message', handler);

  // Return cleanup function
  return () => {
    navigator.serviceWorker.removeEventListener('message', handler);
  };
}

function createPrecachePromise(
  files: SignedImagePayload[],
  messageType: {
    preCache: string;
    progress: string;
    complete: string;
  },
  timeout: number,
  onProgress?: (payload: PrecacheProgressPayload) => void,
) {
  return new Promise<PrecacheCompletePayload>((resolve, reject) => {
    if (!navigator.serviceWorker.controller) {
      reject(new Error('No active service worker'));
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;

    const cleanup = listenToServiceWorker((message) => {
      // cspell:disable-next-line
      if (message.type === messageType.progress && onProgress) {
        // cspell:disable-next-line
        onProgress(message.payload as PrecacheProgressPayload);
      }

      // cspell:disable-next-line
      if (message.type === messageType.complete) {
        cleanup();
        if (timeoutId) clearTimeout(timeoutId);
        // cspell:disable-next-line
        resolve(message.payload as PrecacheCompletePayload);
      }
    });

    // Set a timeout in case the service worker doesn't respond
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Pre-cache timeout - operation took too long'));
    }, timeout);

    // Send the pre-cache message
    // cspell:disable-next-line
    sendMessageToServiceWorker({
      type: messageType.preCache,
      payload: files,
    });
  });
}

function getPrecacheMessageType(prefix?: string) {
  const offset = prefix ? `${prefix}_` : '';

  return {
    preCache: `${offset}PRECACHE_SIGNED`,
    progress: `${offset}PRECACHE_PROGRESS`,
    complete: `${offset}PRECACHE_COMPLETE`,
  };
}

/**
 * Pre-cache signed URLs in the service worker
 * cspell:disable-next-line
 */
export function precacheSignedUrls(
  images: SignedImagePayload[],
  // cspell:disable-next-line
  onProgress?: (payload: PrecacheProgressPayload) => void,
  // cspell:disable-next-line
) {
  return createPrecachePromise(
    images,
    getPrecacheMessageType(),
    // 2 minutes for large image sets
    IMAGE_TIMEOUT,
    onProgress,
  );
}

export function precacheSignedAttachments(
  attachments: SignedImagePayload[],
  onProgress?: (payload: PrecacheProgressPayload) => void,
) {
  return createPrecachePromise(
    attachments,
    getPrecacheMessageType('ATTACHMENTS'),
    ATTACHMENT_TIMEOUT,
    onProgress,
  );
}

/**
 * Get the synthetic URL for a cached image
 */
export function getSyntheticImageUrl(imageId: string): string {
  return `/${EXAM_IMAGE_SYNTHETIC_KEY}/${encodeURIComponent(imageId)}`;
}

export function getSyntheticAttachmentUrl(attachmentId: string): string {
  return `/${EXAM_ATTACHMENT_SYNTHETIC_KEY}/${encodeURIComponent(attachmentId)}`;
}

/**
 * Clear the image cache
 */
export function clearImageCache(): Promise<{ ok: boolean }> {
  return new Promise((resolve, reject) => {
    if (!navigator.serviceWorker.controller) {
      reject(new Error('No active service worker'));
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;

    const cleanup = listenToServiceWorker((message) => {
      if (message.type === 'CACHE_CLEARED') {
        cleanup();
        if (timeoutId) clearTimeout(timeoutId);
        resolve(message.payload as { ok: boolean });
      }
    });

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Clear cache timeout'));
    }, 10000);

    sendMessageToServiceWorker({
      type: 'CLEAR_CACHE',
    });
  });
}

/**
 * Get cache status (count and keys)
 */
export function getCacheStatus(): Promise<CacheStatusPayload> {
  return new Promise((resolve, reject) => {
    if (!navigator.serviceWorker.controller) {
      reject(new Error('No active service worker'));
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;

    const cleanup = listenToServiceWorker((message) => {
      if (message.type === 'CACHE_STATUS') {
        cleanup();
        if (timeoutId) clearTimeout(timeoutId);
        resolve(message.payload as CacheStatusPayload);
      }
    });

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Get cache status timeout'));
    }, 10000);

    sendMessageToServiceWorker({
      type: 'GET_CACHE_STATUS',
    });
  });
}

/**
 * Check if service worker is supported and active
 */
export function isServiceWorkerActive(): boolean {
  return 'serviceWorker' in navigator && navigator.serviceWorker.controller !== null;
}

/**
 * Check if service worker is supported
 */
export function isServiceWorkerSupported(): boolean {
  return 'serviceWorker' in navigator;
}
